import clsx from 'clsx';
import styles from './Welcome.module.scss';
import Guide from '../../../assets/img/guide.svg?react';
import OrdersAndFinance from '../../../assets/img/orders.svg?react';
import ServiceAndCompliance from '../../../assets/img/service-and-compliance.svg?react';
import Software from '../../../assets/img/software.svg?react';
import Articles from '../../../assets/img/articles-supplier.svg?react';
import SupplierOrders from '../../../assets/img/orders-supplier.svg?react';
import WarrantyRequest from '../../../assets/img/warranty-request.svg?react';
import Maintenance from '../../../assets/img/maintenance.svg?react';
import BillingDocuments from '../../../assets/img/bills.svg?react';
import Docs from '../../../assets/img/docs.svg?react';
import CartIcon from '../../../assets/icons/cart.svg?react';
import CloseIcon from '../../../assets/icons/close-button-small.svg?react';
import { Card } from 'components/uikit/Card';
import { QueryFactory } from 'services/api';
import { Loading } from 'components/uikit/suspense/Loading';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import {
  ActionText,
  ActionTextColor,
} from '../../../components/uikit/ActionText.tsx';
import {
  Button,
  ButtonColor,
} from '../../../components/uikit/buttons/Button.tsx';
import { IconButton } from 'components/uikit/IconButton.tsx';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Links } from '../../../application/constants/links.ts';
import { pagingSortingToBackendRequest } from '../../../helpers/pagination-helper.ts';
import {
  UserQuery,
  UserRole,
  WarrantyRequestBasketQuery,
} from 'services/api/api-client.ts';
import { FeatureFlags } from '../../../application/constants/env-variables.ts';

export const WelcomePageCategory = 'Welcome page';

export enum TypesOfCard {
  Download = 'Download',
  Information = 'Information',
  SelfService = 'SelfService',
}

export type LinkType = {
  name: string;
  url: string;
  allowedRoles?: UserRole[];
};

const isEnabled = (
  userRoles: QueryFactory.UserRole[],
  allowedRoles: QueryFactory.UserRole[],
) => {
  const setAllowedRoles = new Set(allowedRoles);

  for (const userRole of userRoles) {
    if (setAllowedRoles.has(userRole)) {
      return true;
    }
  }

  return false;
};

export const WelcomePage = () => {
  const userQuery = UserQuery.useGetCurrentUserInfoQuery();

  const userRoles = userQuery.data?.claimRoles ?? [];
  const isSupplier = userRoles.includes(UserRole.Supplier);
  const isAdmin = userRoles.includes(UserRole.Admin);

  const i18n = useScopedTranslation('Page.WelcomePage');
  const navigate = useNavigate();

  const basketQuery = WarrantyRequestBasketQuery.useGetQuery(
    {
      ...pagingSortingToBackendRequest({ perPage: 10, page: 1 }),
    },
    {
      enabled:
        FeatureFlags.isNotificationTooltipEnabled() &&
        isEnabled(userRoles, [UserRole.Admin, UserRole.Garantie]),
      throwOnError: false,
      staleTime: 60 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );
  const data = basketQuery.data;
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);

  useEffect(() => {
    setIsTooltipVisible(!!data?.records.length);
  }, [data]);

  return (
    <>
      <Loading loading={userQuery.isLoading}>
        {userQuery.isFetched && (
          <div className={styles.welcomeContainer}>
            <div className={styles.section}>
              <div className={styles.title}>{i18n.t('information')}</div>
              <div
                style={{ flexWrap: 'wrap' }}
                className={clsx(
                  styles.rowContainer,
                  styles.columnGap,
                  styles.rowGap,
                )}
              >
                <Card
                  typeOfCard={TypesOfCard.Information}
                  title={i18n.t('orders_and_finance')}
                  links={ordersAndFinanceLinks}
                >
                  <OrdersAndFinance />
                </Card>
                <Card
                  typeOfCard={TypesOfCard.Information}
                  title={i18n.t('service_and_compliance')}
                  links={serviceAndComplianceLinks}
                >
                  <ServiceAndCompliance />
                </Card>
              </div>
            </div>
            <div className={styles.section}>
              <div className={styles.title}>{i18n.t('download')}</div>
              <div
                style={{ flexWrap: 'wrap' }}
                className={clsx(
                  styles.rowContainer,
                  styles.columnGap,
                  styles.rowGap,
                )}
              >
                <>
                  <Card
                    typeOfCard={TypesOfCard.Download}
                    title={i18n.t('billing_documents')}
                    links={billingDocumentLinks}
                  >
                    <BillingDocuments />
                  </Card>
                  <Card
                    typeOfCard={TypesOfCard.Download}
                    title={i18n.t('documents')}
                    links={documentsLinks}
                  >
                    <Docs />
                  </Card>
                  <Card
                    typeOfCard={TypesOfCard.Download}
                    title={i18n.t('instructions')}
                    links={instructionsLinks}
                  >
                    <Guide />
                  </Card>
                  <Card
                    typeOfCard={TypesOfCard.Download}
                    title={i18n.t('software_card_title')}
                    links={softwareLinks}
                  >
                    <Software />
                  </Card>
                </>
                {isSupplier && !isAdmin && (
                  <div className={styles.section}>
                    <Card
                      typeOfCard={TypesOfCard.Download}
                      title={i18n.t('articles')}
                      links={articlesLinks}
                    >
                      <Articles />
                    </Card>
                    <Card
                      typeOfCard={TypesOfCard.Download}
                      title={i18n.t('orders_and_finance')}
                      links={supplierOrderLinks}
                    >
                      <SupplierOrders />
                    </Card>
                    <Card
                      typeOfCard={TypesOfCard.Download}
                      title={i18n.t('documents')}
                      links={supplierDocumentsLinks}
                    >
                      <Docs />
                    </Card>
                    <div
                      className={clsx(styles.columnContainer, styles.rowGap)}
                    >
                      <Card
                        typeOfCard={TypesOfCard.Download}
                        links={[
                          {
                            name: 'contract_overview',
                            url: 'contract-overview',
                          },
                        ]}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className={styles.section}>
                <div className={styles.title}>{i18n.t('self_service')}</div>
                <div
                  style={{ flexWrap: 'wrap' }}
                  className={clsx(
                    styles.rowContainer,
                    styles.columnGap,
                    styles.rowGap,
                  )}
                >
                  <Card
                    typeOfCard={TypesOfCard.SelfService}
                    title={i18n.t('spare_parts_warranty')}
                    links={sparePartsWarrantyLinks}
                  >
                    <WarrantyRequest />
                  </Card>
                  {FeatureFlags.isMainboardReplacerEnabled() && (
                    <Card
                      typeOfCard={TypesOfCard.SelfService}
                      title={i18n.t('maintenance_and_repair')}
                      links={maintenanceLinks}
                    >
                      <Maintenance />
                    </Card>
                  )}
                  {/* For future integration with Prisma Cloud*/}
                  {/*<Card*/}
                  {/*  typeOfCard={TypesOfCard.SelfService}*/}
                  {/*  title={i18n.t('prisma_cloud')}*/}
                  {/*  links={prismaCloudLinks}*/}
                  {/*>*/}
                  {/*  <Web />*/}
                  {/*</Card>*/}
                </div>
              </div>
            </div>
          </div>
        )}
        {isTooltipVisible && FeatureFlags.isNotificationTooltipEnabled() && (
          <Tooltip
            titleContent={
              <div className={styles.rowContainerTooltipHeader}>
                <div className={styles.rowContainer4}>
                  <CartIcon />
                  <div>{i18n.t('tooltip_title')}</div>
                </div>
                <div className={styles.tooltipCloseIcon}>
                  <IconButton
                    onClick={() => setIsTooltipVisible(false)}
                    type={'destroy'}
                    children={<CloseIcon />}
                  />
                </div>
              </div>
            }
            bodyContent={
              <div className={styles.whiteSpace}>{i18n.t('tooltip_body')}</div>
            }
            footerContent={
              <div className={styles.rowContainerFooter}>
                <ActionText
                  className={styles.actionTextRemind}
                  text={i18n.t('tooltip_remind_later')}
                  color={ActionTextColor.Dark}
                  onClickHandler={() => setIsTooltipVisible(false)}
                />
                <Button
                  className={styles.tooltipButton}
                  color={ButtonColor.SecondaryDark}
                  title={i18n.t('tooltip_go_to_cart')}
                  onClick={() => {
                    void navigate(Links.Authorized.WarrantyRequest.link());
                  }}
                />
              </div>
            }
            className={styles.tooltip}
          />
        )}
      </Loading>
    </>
  );
};

const ordersAndFinanceLinks: LinkType[] = [
  {
    name: 'order_list',
    url: Links.Authorized.OrderList.route,
    allowedRoles: [UserRole.Customer, UserRole.Garantie, UserRole.Standard],
  },
  {
    name: 'outstanding_receivables',
    url: Links.Authorized.OutstandingReceivables.route,
    allowedRoles: [UserRole.Customer, UserRole.Garantie, UserRole.Standard],
  },
  // {
  //   name: 'shipment_tracking',
  //   url: 'shipment-tracking',
  // },
];

const serviceAndComplianceLinks: LinkType[] = [
  {
    name: 'qualifications',
    url: Links.Authorized.Qualifications.route,
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Techniker,
      UserRole.TechnikerE,
      UserRole.HaendlerDe,
      UserRole.HaendlerEk,
    ],
  },
  {
    name: 'device_overview',
    url: Links.Authorized.DeviceOverview.route,
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Techniker,
      UserRole.Techniker2,
      UserRole.TechnikerE,
    ],
  },
  {
    name: 'repairs',
    url: Links.Authorized.Repairs.route,
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Techniker,
      UserRole.Techniker2,
      UserRole.TechnikerE,
    ],
  },
];

const billingDocumentLinks: LinkType[] = [
  {
    name: 'price_lists',
    url: Links.Authorized.PriceLists.route,
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Toechter,
      UserRole.HaendlerEk,
    ],
  },
  {
    name: 'telehealth_billing',
    url: Links.Authorized.TelehealthBilling.route,
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Toechter,
    ],
  },
  {
    name: 'serial_number',
    url: 'serial-number',
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Techniker,
      UserRole.Techniker2,
      UserRole.Toechter,
      UserRole.TechnikerE,
      UserRole.HaendlerDe,
      UserRole.HaendlerEk,
    ],
  },
];

const documentsLinks: LinkType[] = [
  {
    name: 'tech_updates',
    url: Links.Authorized.TechUpdates.route,
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Techniker,
      UserRole.Techniker2,
      UserRole.TechnikerE,
      UserRole.HaendlerDe,
      UserRole.HaendlerEk,
    ],
  },
  {
    name: 'service_presentation',
    url: Links.Authorized.ServicePresentation.route,
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Techniker,
      UserRole.Techniker2,
      UserRole.TechnikerE,
      UserRole.HaendlerDe,
      UserRole.HaendlerEk,
    ],
  },
];

const instructionsLinks: LinkType[] = [
  {
    name: 'service_and_repair',
    url: Links.Authorized.ServiceAndRepair.route,
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Techniker,
      UserRole.Techniker2,
      UserRole.TechnikerE,
      UserRole.HaendlerDe,
      UserRole.HaendlerEk,
    ],
  },
  {
    name: 'settings_and_use',
    url: 'settings-and-use',
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Techniker,
      UserRole.Toechter,
      UserRole.TechnikerE,
      UserRole.HaendlerDe,
      UserRole.HaendlerEk,
      UserRole.SemiAdmin,
      UserRole.SoftwareAccess,
      UserRole.FirmwareAccess,
    ],
  },
];

const softwareLinks: LinkType[] = [
  {
    name: 'firmware',
    url: 'firmware',
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Techniker,
      UserRole.Techniker2,
      UserRole.TechnikerE,
      UserRole.HaendlerDe,
      UserRole.HaendlerEk,
      UserRole.SemiAdmin,
      UserRole.FirmwareAccess,
    ],
  },
  {
    name: 'software',
    url: 'software',
    allowedRoles: [
      UserRole.Customer,
      UserRole.Garantie,
      UserRole.Standard,
      UserRole.Techniker,
      UserRole.Techniker2,
      UserRole.TechnikerE,
      UserRole.HaendlerDe,
      UserRole.HaendlerEk,
      UserRole.SemiAdmin,
      UserRole.SoftwareAccess,
    ],
  },
];
// For future integration with Prisma Cloud
// const prismaCloudLinks: LinkType[] = [
//   {
//     name: 'upload_registration_list',
//     url: 'upload-registration-list',
//   },
//   {
//     name: 'request_mainboard_certificate',
//     url: 'request-mainboard-cerfiticate',
//   },
// ];

const sparePartsWarrantyLinks: LinkType[] = [
  {
    name: 'create_warranty_request',
    url: Links.Authorized.WarrantyRequest.route,
    allowedRoles: [UserRole.Garantie, UserRole.TechnikerE],
  },
  {
    name: 'warranty_requests_history',
    url: Links.Authorized.WarrantyRequestHistory.route,
    allowedRoles: [UserRole.Garantie, UserRole.TechnikerE],
  },
];

const maintenanceLinks: LinkType[] = [
  {
    name: 'request_mainboard_certificate',
    url: Links.Authorized.MainboardReplacerForm.route,
    allowedRoles: [UserRole.ClevelandAccess],
  },
];

// supplier block, maybe we will use it in the future, but for now it's useless
const articlesLinks: LinkType[] = [
  {
    name: 'articles_overview',
    url: 'articles',
    allowedRoles: [UserRole.Supplier],
  },
  {
    name: 'print_data',
    url: 'print-data',
    allowedRoles: [UserRole.Supplier],
  },
];

const supplierOrderLinks: LinkType[] = [
  {
    name: 'order_proposals',
    url: 'order-proposals',
    allowedRoles: [UserRole.Supplier],
  },
  {
    name: 'outstanding_deliveries',
    url: 'outstanding-deliveries',
    allowedRoles: [UserRole.Supplier],
  },
];

const supplierDocumentsLinks: LinkType[] = [
  {
    name: 'lieferantenportal',
    url: 'dokumentation-Lieferantenportal',
    allowedRoles: [UserRole.Supplier],
  },
  {
    name: 'documentation',
    url: 'documentation-supplier-portal',
    allowedRoles: [UserRole.Supplier],
  },
];
